import { IconButton, PopoverMenu } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { More } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IChatToggleMuteAction } from '../../../common/reducers/types';
import {
  selectIsMuted,
  selectItemsLength,
} from '../../../common/selectors/chat';
import { ACTIONS } from '../../../constants';
import { preventEvent } from '../../../util';
import { cookie } from '../../../util/cookie';
import { EndLiveChatModal } from '../EndLiveChatModal';

type LiveChatActionsProps = {
  dataHook: string;
};

const toggleMute = (isMuted: boolean): IChatToggleMuteAction => ({
  type: ACTIONS.CHAT_TOGGLE_MUTE,
  isMuted,
});

const cookieName = 'isMuted';
const delayToggleMuteMs = 100; // delay changing mute state after click on popover item(because popover closes with delay)
const popoverTriggerElement = (
  <IconButton skin="light">
    <More />
  </IconButton>
);

export const LiveChatActions: FC<LiveChatActionsProps> = ({ dataHook }) => {
  const { t } = useTranslation();
  const itemsLength = useSelector(selectItemsLength);
  const isMuted = useSelector(selectIsMuted);
  const dispatch = useDispatch();
  const toggleMuteTimeoutId = useRef<number | null>(null);
  const [isModalOpened, setModalOpened] = useState(false);

  const onToggleMute = useCallback(() => {
    if (toggleMuteTimeoutId.current !== null) {
      window.clearTimeout(toggleMuteTimeoutId.current);
    }

    toggleMuteTimeoutId.current = window.setTimeout(() => {
      const mute = !isMuted;

      dispatch(toggleMute(mute));
      cookie.set(cookieName, mute);
    }, delayToggleMuteMs);
  }, [isMuted, dispatch, toggleMuteTimeoutId]);

  const onCloseModal = useCallback(() => setModalOpened(false), []);
  const onOpenModal = useCallback(() => setModalOpened(true), []);

  useEffect(() => {
    /**
     * Check is muted sound for each message
     * It can be muted on another tab
     */
    if (cookie.has(cookieName)) {
      const mute = cookie.is(cookieName, 'true');

      if (isMuted !== mute) {
        dispatch(toggleMute(mute));
      }
    }
  }, [itemsLength]);

  return (
    <div onClick={preventEvent}>
      <PopoverMenu
        placement="bottom-end"
        dataHook={dataHook}
        triggerElement={popoverTriggerElement}
      >
        <PopoverMenu.MenuItem
          text={isMuted ? t('unmute_widget') : t('mute_widget')}
          onClick={onToggleMute}
        />
        <PopoverMenu.MenuItem
          text={t('live_chat_close_btn')}
          onClick={onOpenModal}
        />
      </PopoverMenu>
      <EndLiveChatModal isOpen={isModalOpened} onCloseModal={onCloseModal} />
    </div>
  );
};
